import { zodResolver } from '@hookform/resolvers/zod'
import { envVars } from '@repo/sdk/env-vars'
import { getHealthCheck } from '@repo/sdk-generated'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import type { AppLanguageCode } from '@/lib/i18n'
import { buildSeo } from './-lib/seo'
import {
	resolveServiceHealthBaseUrl,
	resolveServiceHealthStateFromCheck
} from './-lib/service-health'
import { createSiteTranslate } from './-lib/site-i18n'

const SERVICE_HEALTH_TIMEOUT_MS = 1500

export const Route = createFileRoute('/(public)/support')({
	ssr: true,
	head: ({ match }) => {
		const { appLanguage } = match.context
		const t = createSiteTranslate(appLanguage)
		return buildSeo({
			appLanguage,
			path: '/support',
			title: t('site:seo.support.title'),
			description: t('site:seo.support.description')
		})
	},
	loader: async () => ({
		serviceHealth: await loadServiceHealth()
	}),
	component: SupportPage
})

function loadServiceHealth() {
	return resolveServiceHealthStateFromCheck({
		healthCheck: async (signal) => {
			const response = await getHealthCheck({
				baseUrl: resolveHealthCheckBaseUrl(),
				signal,
				throwOnError: false
			})
			return response.data
		},
		timeoutMs: SERVICE_HEALTH_TIMEOUT_MS
	})
}

function resolveHealthCheckBaseUrl() {
	return resolveServiceHealthBaseUrl({
		apiEndpoint: envVars.VITE_API_ENDPOINT,
		isDev: import.meta.env.DEV
	})
}

function ContactFormCard({
	appLanguage,
	onSuccess
}: {
	appLanguage: AppLanguageCode
	onSuccess: () => void
}) {
	const t = createSiteTranslate(appLanguage)

	const schema = z.object({
		nome: z.string().min(1, t('site:support.form.errors.name_required')),
		email: z.email(t('site:support.form.errors.email_invalid')),
		cat: z.string(),
		oggetto: z
			.string()
			.min(1, t('site:support.form.errors.subject_required')),
		msg: z.string().min(1, t('site:support.form.errors.message_required'))
	})

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues: {
			nome: '',
			email: '',
			cat: t('site:support.form.categories.scanner'),
			oggetto: '',
			msg: ''
		}
	})

	const handleSubmit = form.handleSubmit(() => {
		form.reset()
		onSuccess()
	})

	return (
		<div className='card'>
			<h2>{t('site:support.form.card_heading')}</h2>
			<p className='card-sub'>{t('site:support.form.card_sub')}</p>
			<form onSubmit={handleSubmit}>
				<div className='field-row'>
					<Controller
						control={form.control}
						name='nome'
						render={({ field, fieldState }) => (
							<div className='field'>
								<label htmlFor='nome'>
									{t('site:support.form.fields.name_label')}{' '}
									<span className='req'>*</span>
								</label>
								<input
									{...field}
									aria-invalid={
										fieldState.invalid ? true : undefined
									}
									className='inp'
									id='nome'
									placeholder={t(
										'site:support.form.fields.name_placeholder'
									)}
									type='text'
								/>
								{fieldState.error && (
									<div className='field-error'>
										{fieldState.error.message}
									</div>
								)}
							</div>
						)}
					/>
					<Controller
						control={form.control}
						name='email'
						render={({ field, fieldState }) => (
							<div className='field'>
								<label htmlFor='email'>
									{t('site:support.form.fields.email_label')}{' '}
									<span className='req'>*</span>
								</label>
								<input
									{...field}
									aria-invalid={
										fieldState.invalid ? true : undefined
									}
									className='inp'
									id='email'
									placeholder={t(
										'site:support.form.fields.email_placeholder'
									)}
									type='email'
								/>
								{fieldState.error && (
									<div className='field-error'>
										{fieldState.error.message}
									</div>
								)}
							</div>
						)}
					/>
				</div>

				<Controller
					control={form.control}
					name='cat'
					render={({ field }) => (
						<div className='field'>
							<label htmlFor='cat'>
								{t('site:support.form.fields.category_label')}
							</label>
							<select {...field} className='sel' id='cat'>
								<option
									value={t(
										'site:support.form.categories.scanner'
									)}
								>
									{t('site:support.form.categories.scanner')}
								</option>
								<option
									value={t(
										'site:support.form.categories.collection'
									)}
								>
									{t(
										'site:support.form.categories.collection'
									)}
								</option>
								<option
									value={t(
										'site:support.form.categories.account'
									)}
								>
									{t('site:support.form.categories.account')}
								</option>
								<option
									value={t(
										'site:support.form.categories.subscription'
									)}
								>
									{t(
										'site:support.form.categories.subscription'
									)}
								</option>
								<option
									value={t(
										'site:support.form.categories.card_report'
									)}
								>
									{t(
										'site:support.form.categories.card_report'
									)}
								</option>
								<option
									value={t(
										'site:support.form.categories.other'
									)}
								>
									{t('site:support.form.categories.other')}
								</option>
							</select>
						</div>
					)}
				/>

				<Controller
					control={form.control}
					name='oggetto'
					render={({ field, fieldState }) => (
						<div className='field'>
							<label htmlFor='oggetto'>
								{t('site:support.form.fields.subject_label')}{' '}
								<span className='req'>*</span>
							</label>
							<input
								{...field}
								aria-invalid={
									fieldState.invalid ? true : undefined
								}
								className='inp'
								id='oggetto'
								placeholder={t(
									'site:support.form.fields.subject_placeholder'
								)}
								type='text'
							/>
							{fieldState.error && (
								<div className='field-error'>
									{fieldState.error.message}
								</div>
							)}
						</div>
					)}
				/>

				<Controller
					control={form.control}
					name='msg'
					render={({ field, fieldState }) => (
						<div className='field'>
							<label htmlFor='msg'>
								{t('site:support.form.fields.message_label')}{' '}
								<span className='req'>*</span>
							</label>
							<textarea
								{...field}
								aria-invalid={
									fieldState.invalid ? true : undefined
								}
								className='ta'
								id='msg'
								placeholder={t(
									'site:support.form.fields.message_placeholder'
								)}
							/>
							<div className='form-note'>
								{t('site:support.form.note_device')}
							</div>
							{fieldState.error && (
								<div className='field-error'>
									{fieldState.error.message}
								</div>
							)}
						</div>
					)}
				/>

				<button
					className='btn btn-pri'
					style={{ width: '100%' }}
					type='submit'
				>
					{t('site:support.form.submit')}
				</button>
				<div
					className='form-note'
					style={{ marginTop: '12px', textAlign: 'center' }}
				>
					{t('site:support.form.privacy_note_prefix')}{' '}
					<Link style={{ color: 'var(--accent)' }} to='/privacy'>
						{t('site:support.form.privacy_link_text')}
					</Link>
					.
				</div>
			</form>
		</div>
	)
}

function SupportPage() {
	const { appLanguage } = Route.useRouteContext()
	const { serviceHealth } = Route.useLoaderData()
	const t = createSiteTranslate(appLanguage)
	const [showToast, setShowToast] = useState(false)

	const handleFormSuccess = () => {
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3200)
	}

	return (
		<>
			<section className='doc-hero'>
				<div className='wrap'>
					<span className='eyebrow'>
						{t('site:support.hero.eyebrow')}
					</span>
					<h1>{t('site:support.hero.title')}</h1>
					<p>{t('site:support.hero.description')}</p>
				</div>
			</section>

			<div className='wrap' style={{ padding: '48px 0 64px' }}>
				<div className='sup-grid'>
					<ContactFormCard
						appLanguage={appLanguage}
						onSuccess={handleFormSuccess}
					/>

					<div className='sup-side'>
						<div className='card' style={{ marginBottom: '20px' }}>
							<h2
								style={{
									fontSize: '18px',
									marginBottom: '14px'
								}}
							>
								{t('site:support.contacts.heading')}
							</h2>
							<div className='ct'>
								<span className='ct-ic'>
									<svg
										aria-hidden='true'
										fill='none'
										height='20'
										viewBox='0 0 24 24'
										width='20'
									>
										<rect
											height='14'
											rx='3'
											stroke='oklch(78% 0.14 85)'
											strokeWidth='1.6'
											width='18'
											x='3'
											y='5'
										/>
										<path
											d='M4 7l8 6 8-6'
											stroke='oklch(78% 0.14 85)'
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth='1.6'
										/>
									</svg>
								</span>
								<div>
									<b>
										{t('site:support.contacts.email_label')}
									</b>
									<span>
										<a
											href={`mailto:${t('site:support.contacts.email_value')}`}
										>
											{t(
												'site:support.contacts.email_value'
											)}
										</a>
									</span>
								</div>
							</div>
							<div className='ct'>
								<span className='ct-ic'>
									<svg
										aria-hidden='true'
										fill='none'
										height='20'
										viewBox='0 0 24 24'
										width='20'
									>
										<circle
											cx='12'
											cy='12'
											r='9'
											stroke='oklch(78% 0.14 85)'
											strokeWidth='1.6'
										/>
										<path
											d='M12 7v5l3.5 2'
											stroke='oklch(78% 0.14 85)'
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth='1.6'
										/>
									</svg>
								</span>
								<div>
									<b>
										{t('site:support.contacts.hours_label')}
									</b>
									<span>
										{t('site:support.contacts.hours_value')}
									</span>
								</div>
							</div>
							<div className='ct'>
								<span className='ct-ic'>
									<svg
										aria-hidden='true'
										fill='none'
										height='20'
										viewBox='0 0 24 24'
										width='20'
									>
										<path
											d='M12 3a7 7 0 00-7 7c0 4.5 7 11 7 11s7-6.5 7-11a7 7 0 00-7-7z'
											stroke='oklch(78% 0.14 85)'
											strokeLinejoin='round'
											strokeWidth='1.6'
										/>
										<circle
											cx='12'
											cy='10'
											r='2.4'
											stroke='oklch(78% 0.14 85)'
											strokeWidth='1.6'
										/>
									</svg>
								</span>
								<div>
									<b>
										{t(
											'site:support.contacts.response_label'
										)}
									</b>
									<span>
										{t(
											'site:support.contacts.response_value'
										)}
									</span>
								</div>
							</div>
						</div>

						<div className='card'>
							<h2
								style={{
									fontSize: '18px',
									marginBottom: '14px'
								}}
							>
								{t('site:support.status.heading')}
							</h2>
							<div
								className={`status-row status-row-${serviceHealth}`}
							>
								<span className='status-dot' />
								<span>
									{serviceHealth === 'operational'
										? t('site:support.status.ok')
										: t('site:support.status.degraded')}
								</span>
							</div>
							<p
								style={{
									color: 'var(--faint)',
									fontSize: '13px',
									marginTop: '12px'
								}}
							>
								{serviceHealth === 'operational'
									? t('site:support.status.description')
									: t(
											'site:support.status.degraded_description'
										)}
							</p>
						</div>
					</div>
				</div>

				<div style={{ margin: '64px auto 0', maxWidth: '780px' }}>
					<div className='center' style={{ marginBottom: '32px' }}>
						<span className='eyebrow'>
							{t('site:support.faq.eyebrow')}
						</span>
						<h2 className='h-sec' style={{ fontSize: '30px' }}>
							{t('site:support.faq.heading')}
						</h2>
					</div>
					<div className='faq' style={{ marginTop: '0' }}>
						<details className='qa' open>
							<summary>
								{t('site:support.faq.q1.question')}
								<span className='chev'>
									<svg
										aria-hidden='true'
										fill='none'
										height='18'
										viewBox='0 0 24 24'
										width='18'
									>
										<path
											d='M6 9l6 6 6-6'
											stroke='currentColor'
											strokeLinecap='round'
											strokeWidth='2'
										/>
									</svg>
								</span>
							</summary>
							<div className='ans'>
								{t('site:support.faq.q1.answer')}
							</div>
						</details>

						<details className='qa'>
							<summary>
								{t('site:support.faq.q2.question')}
								<span className='chev'>
									<svg
										aria-hidden='true'
										fill='none'
										height='18'
										viewBox='0 0 24 24'
										width='18'
									>
										<path
											d='M6 9l6 6 6-6'
											stroke='currentColor'
											strokeLinecap='round'
											strokeWidth='2'
										/>
									</svg>
								</span>
							</summary>
							<div className='ans'>
								{t('site:support.faq.q2.answer')}
							</div>
						</details>

						<details className='qa'>
							<summary>
								{t('site:support.faq.q3.question')}
								<span className='chev'>
									<svg
										aria-hidden='true'
										fill='none'
										height='18'
										viewBox='0 0 24 24'
										width='18'
									>
										<path
											d='M6 9l6 6 6-6'
											stroke='currentColor'
											strokeLinecap='round'
											strokeWidth='2'
										/>
									</svg>
								</span>
							</summary>
							<div className='ans'>
								{t('site:support.faq.q3.answer')}
							</div>
						</details>

						<details className='qa'>
							<summary>
								{t('site:support.faq.q4.question')}
								<span className='chev'>
									<svg
										aria-hidden='true'
										fill='none'
										height='18'
										viewBox='0 0 24 24'
										width='18'
									>
										<path
											d='M6 9l6 6 6-6'
											stroke='currentColor'
											strokeLinecap='round'
											strokeWidth='2'
										/>
									</svg>
								</span>
							</summary>
							<div className='ans'>
								{t('site:support.faq.q4.answer')}
							</div>
						</details>

						<details className='qa'>
							<summary>
								{t('site:support.faq.q5.question')}
								<span className='chev'>
									<svg
										aria-hidden='true'
										fill='none'
										height='18'
										viewBox='0 0 24 24'
										width='18'
									>
										<path
											d='M6 9l6 6 6-6'
											stroke='currentColor'
											strokeLinecap='round'
											strokeWidth='2'
										/>
									</svg>
								</span>
							</summary>
							<div className='ans'>
								{t('site:support.faq.q5.answer_part1')}{' '}
								<a
									href={`mailto:${t('site:support.faq.q5.answer_email')}`}
									style={{ color: 'var(--accent)' }}
								>
									{t('site:support.faq.q5.answer_email')}
								</a>
								{t('site:support.faq.q5.answer_part2')}{' '}
								<Link
									style={{ color: 'var(--accent)' }}
									to='/privacy'
								>
									{t(
										'site:support.faq.q5.answer_policy_link'
									)}
								</Link>
								.
							</div>
						</details>
					</div>
				</div>
			</div>

			<div className={showToast ? 'toast show' : 'toast'}>
				<span className='tk'>
					<svg
						aria-hidden='true'
						fill='none'
						height='18'
						viewBox='0 0 24 24'
						width='18'
					>
						<path
							d='M5 13l4 4L19 7'
							stroke='currentColor'
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth='2.4'
						/>
					</svg>
				</span>
				<span>{t('site:support.toast.message')}</span>
			</div>
		</>
	)
}
